// tabs/GanttTab.js
// Диаграмма Ганта v2: панели следуют из общего checkbox-фильтра области данных
import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./GanttTab.css";
import ScopeFilter from "../../../components/ScopeFilter/ScopeFilter";
import useScopeOptions from "../../../components/ScopeFilter/useScopeOptions";
import { useScopeCache } from "../../../hooks/useScopeCache";
import { DateTimeInput } from "../../../components/DateTimeInput";
import { isEmptyScope } from "../../../utils/scopeSelection";
import { formatShortInTimezone, formatAxisTick } from "../../../utils/formatInTimezone";
import { pickGanttAxisTimezone, describeGanttAxisTimezone } from "./ganttTimezone";
import { MODES, formatValue } from "../../../components/DateTimeInput/valueFormat";
import {
  GANTT_MODES,
  MAX_GANTT_PANELS,
  buildGanttTargets,
  describeGanttTarget,
  panelValueMax,
} from "./ganttV2Model";
import { resolveScopeCameras } from "../../../components/ScopeFilter/scopeCameras";
import { useGanttV2Panels } from "./useGanttV2Panels";
import { useGanttMetrics, DEFAULT_GANTT_METRIC } from "./useGanttMetrics";
import { useDraftScope } from "../../../hooks/useDraftScope";

/*
 * FILTER-UX01 + GANTT02 (ТЗ v4 §19, FE-FIX16).
 *
 * Набор панелей — отмеченные камеры (и их зоны) в ScopeFilter, том же
 * компоненте, которым фильтруются «Сводка» и «Контроль». Галочки и период —
 * черновик: панели перестраиваются только по «Применить».
 *
 * Каждая панель — свой запрос gantt-v2 по своей камере/зоне. Данные разных
 * камер не складываются ни в одном месте: общая у панелей только ось времени,
 * и она построена на абсолютном интервале, как того требует §19.2.
 */

const SCOPE_CACHE_KEY = "statistics:gantt";
const MIN_CELL_PX = 2;

const toLocalDT = (date) => formatValue(MODES.DATETIME, new Date(date), false);
const nowLocal = () => toLocalDT(new Date());
const hoursAgoLocal = (h) => toLocalDT(Date.now() - h * 3_600_000);

/** Белый → красный по шкале самой панели: метрики у панелей разные. */
const getHeatColor = (value, max) => {
  if (value == null) return "#f0f0f0";
  const scale = max > 0 ? max : 1;
  const ratio = Math.min(Math.max(value / scale, 0), 1);
  const rest = Math.round(255 * (1 - ratio));
  return `rgb(255,${rest},${rest})`;
};

const calcChartWidth = (totalMs, segmentCount) => {
  const totalMin = totalMs / 60_000;
  const byDuration = Math.max(Math.ceil(totalMin), 400);
  return Math.max(byDuration, segmentCount * MIN_CELL_PX);
};

/** Значение метрики в подписи: нечисловое состояние остаётся «нет данных». */
const formatMetricValue = (segment) => {
  if (!segment || segment.state === "unknown") return "нет данных";
  if (segment.value == null) return "значение не числовое";
  return String(Math.round(segment.value * 100) / 100);
};

export default function GanttTab() {
  const { scope, period, setScope, setPeriod } = useScopeCache(SCOPE_CACHE_KEY, {
    period: { from: hoursAgoLocal(8), to: nowLocal(), mode: "raw", metric: "" },
  });

  // Галочки и даты — черновик; панели строятся по «Применить».
  const scopeDraft = useDraftScope(scope, setScope);
  const [draftRange, setDraftRange] = useState({ from: period.from, to: period.to });
  const levels = useScopeOptions(scopeDraft.draft, { includeZones: true });
  const [hoveredCell, setHoveredCell] = useState(null);
  const tooltipRef = useRef(null);

  const cameras = useMemo(() => resolveScopeCameras(scope, levels), [scope, levels]);
  const cameraIds = useMemo(() => cameras.map((camera) => camera.id), [cameras]);
  const metrics = useGanttMetrics(cameraIds);

  // Метрика из кэша остаётся выбранной, пока она есть в каталоге; иначе берём
  // people_count, а если и его нет — первую доступную.
  const metricOptions = metrics.options;
  const metric = useMemo(() => {
    const available = metricOptions.map((item) => item.metric);
    if (period.metric && available.includes(period.metric)) return period.metric;
    if (available.includes(DEFAULT_GANTT_METRIC)) return DEFAULT_GANTT_METRIC;
    return available[0] || "";
  }, [metricOptions, period.metric]);

  const { targets, total, truncated } = useMemo(
    () => buildGanttTargets(cameras, scope.zoneIds, Object.values(levels.zoneIds.known || {})),
    [cameras, scope.zoneIds, levels.zoneIds.known]
  );

  const draftRangeValid = Boolean(draftRange.from && draftRange.to && draftRange.from < draftRange.to);
  const rangeDirty = draftRange.from !== period.from || draftRange.to !== period.to;
  const applyAll = () => {
    scopeDraft.apply();
    setPeriod({ from: draftRange.from, to: draftRange.to });
  };

  const rangeValid = Boolean(period.from && period.to && period.from < period.to);
  const requested = useMemo(() => ({
    targets,
    metric,
    mode: period.mode || "raw",
    fromIso: rangeValid ? new Date(period.from).toISOString() : "",
    toIso: rangeValid ? new Date(period.to).toISOString() : "",
  }), [targets, metric, period.mode, period.from, period.to, rangeValid]);

  const { panels, loading, loaded, retry } = useGanttV2Panels(requested);

  const readyPanels = useMemo(
    () => Object.values(panels).filter((panel) => panel.status === "ready"),
    [panels]
  );

  // Ось подписывается по display_timezone из ответов: зону определяет backend
  // (timezone_basis = camera_display_timezone), а не браузер.
  const axisTimezone = useMemo(
    () => pickGanttAxisTimezone(readyPanels.map((panel) => panel.data)),
    [readyPanels]
  );

  const totalSegments = useMemo(
    () => readyPanels.reduce((sum, panel) => sum + panel.segments.length, 0),
    [readyPanels]
  );

  const { chartWidth, axisLabels, chartStartMs, chartTotalMs } = useMemo(() => {
    if (!requested.fromIso || !requested.toIso) {
      return { chartWidth: 0, axisLabels: [], chartStartMs: 0, chartTotalMs: 0 };
    }
    const start = new Date(requested.fromIso);
    const end = new Date(requested.toIso);
    const totalMs = end - start;
    const width = calcChartWidth(totalMs, totalSegments);
    const totalMin = Math.ceil(totalMs / 60_000);
    const labelMinuteInterval =
      totalMin <= 120 ? 15 : totalMin <= 360 ? 30 : totalMin <= 1440 ? 60 : 120;
    const multiDay = totalMs > 86_400_000;

    const labels = [];
    for (let m = 0; m < totalMin; m += labelMinuteInterval) {
      const tick = new Date(start.getTime() + m * 60_000);
      labels.push({
        px: (m / totalMin) * width,
        label: formatAxisTick(tick.toISOString(), axisTimezone.tz, multiDay),
      });
    }
    return { chartWidth: width, axisLabels: labels, chartStartMs: start.getTime(), chartTotalMs: totalMs };
  }, [requested.fromIso, requested.toIso, totalSegments, axisTimezone.tz]);

  const minCellPx = Math.max(MIN_CELL_PX, chartTotalMs > 0 && totalSegments > 0
    ? Math.round(chartWidth / Math.max(totalSegments, 1))
    : MIN_CELL_PX);

  const scopeTooWide = isEmptyScope(scope) || targets.length === 0;

  return (
    <div className="gantt-container">
      <div className="gantt-controls-box">
        <div className="gantt-help-row">
          <Link className="gantt-help-link" to="/help?tab=gantt">
            Как контролировать загруженность сотрудников
          </Link>
          <Link className="gantt-help-link" to="/help?tab=shift-analysis">
            Анализ смены и деятельности сотрудников
          </Link>
        </div>

        <div className="gantt-scope-box">
          <ScopeFilter
            value={scopeDraft.draft}
            onChange={scopeDraft.setDraft}
            onApply={applyAll}
            applyDisabled={!draftRangeValid || (!scopeDraft.dirty && !rangeDirty)}
            includeZones
            levels={levels}
            idPrefix="gantt-scope"
            title="Область данных"
          >
            <div className="gantt-filter-row">
              <div className="gantt-field">
                <span className="gantt-label">Начало</span>
                <DateTimeInput
                  className="gantt-select gantt-datetime"
                  value={draftRange.from}
                  max={draftRange.to}
                  onChange={(value) => setDraftRange((prev) => ({ ...prev, from: value }))}
                  ariaLabel="Начало периода"
                />
              </div>
              <div className="gantt-field">
                <span className="gantt-label">Конец</span>
                <DateTimeInput
                  className="gantt-select gantt-datetime"
                  value={draftRange.to}
                  min={draftRange.from}
                  onChange={(value) => setDraftRange((prev) => ({ ...prev, to: value }))}
                  ariaLabel="Конец периода"
                />
              </div>
            </div>
            {!draftRangeValid && (
              <p className="gantt-note gantt-note--warn">Дата начала должна быть раньше даты конца.</p>
            )}
          </ScopeFilter>
        </div>

        <div className="gantt-filter-row">
          <div className="gantt-field">
            <span className="gantt-label">Метрика</span>
            <select
              className="gantt-select"
              value={metric}
              aria-label="Метрика"
              onChange={(e) => setPeriod({ metric: e.target.value })}
            >
              {metricOptions.map((item) => (
                <option key={item.metric} value={item.metric}>
                  {item.display_name || item.metric}
                </option>
              ))}
            </select>
          </div>

          <div className="gantt-field">
            <span className="gantt-label">Режим</span>
            <div className="gantt-modes" role="group" aria-label="Режим данных">
              {GANTT_MODES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  title={item.hint}
                  className={`gantt-mode-btn${(period.mode || "raw") === item.id ? " gantt-mode-btn--active" : ""}`}
                  onClick={() => setPeriod({ mode: item.id })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

        </div>

        {metrics.status === "restricted" && (
          <p className="gantt-note">
            Каталог метрик недоступен для вашей роли — показана метрика по умолчанию.
          </p>
        )}
        {truncated && (
          <p className="gantt-note gantt-note--warn">
            Выбрано {total}, показаны первые {MAX_GANTT_PANELS}. Уточните выбор,
            чтобы увидеть остальные.
          </p>
        )}
        {loading && (
          <p className="gantt-note" role="status">
            Загружено {loaded} из {requested.targets.length}
          </p>
        )}
      </div>

      {targets.length > 0 && rangeValid ? (
        <div className="gantt-chart-wrapper">
          <div className="gantt-timezone-note">{describeGanttAxisTimezone(axisTimezone)}</div>
          <div className="gantt-chart" style={{ width: chartWidth + 160 }}>

            <div className="gantt-time-axis">
              <div className="gantt-row-label-placeholder" />
              <div style={{ position: "relative", width: chartWidth, height: 30 }}>
                {axisLabels.map(({ px, label }) => (
                  <React.Fragment key={px}>
                    <div
                      style={{ position: "absolute", left: px, bottom: 0, width: 1, height: 6, background: "#bbb" }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        left: px,
                        top: 4,
                        fontSize: 10,
                        color: "#888",
                        whiteSpace: "nowrap",
                        transform: "translateX(-50%)",
                        userSelect: "none",
                        pointerEvents: "none",
                      }}
                    >
                      {label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="gantt-rows">
              {targets.map((target) => {
                const panel = panels[target.key];
                const segments = panel?.status === "ready" ? panel.segments : [];
                const maxValue = panelValueMax(segments);
                const timezone = panel?.data?.display_timezone;

                return (
                  <div key={target.key} className="gantt-row" data-testid="gantt-panel">
                    <div className="gantt-room-label" title={describeGanttTarget(target)}>
                      {describeGanttTarget(target)}
                    </div>

                    <div
                      className="gantt-row-track"
                      style={{ width: chartWidth, height: 28, margin: "4px 0" }}
                    >
                      {segments.map((segment, index) => {
                        const leftPx = chartTotalMs > 0
                          ? ((segment.startMs - chartStartMs) / chartTotalMs) * chartWidth
                          : 0;
                        if (leftPx < 0 || leftPx > chartWidth) return null;
                        const endPx = segment.endMs
                          ? ((segment.endMs - chartStartMs) / chartTotalMs) * chartWidth
                          : leftPx + minCellPx;
                        const width = Math.min(Math.max(endPx - leftPx, MIN_CELL_PX), chartWidth - leftPx);

                        return (
                          <div
                            key={`${segment.startMs}-${index}`}
                            className={`gantt-cell${segment.state === "unknown" ? " gantt-cell--unknown" : ""}`}
                            style={{
                              position: "absolute",
                              left: leftPx,
                              top: 0,
                              width,
                              height: "100%",
                              backgroundColor: segment.state === "unknown"
                                ? undefined
                                : getHeatColor(segment.value, maxValue),
                              borderRadius: 1,
                            }}
                            onMouseEnter={(e) => setHoveredCell({
                              segment,
                              title: describeGanttTarget(target),
                              timezone,
                              rect: e.currentTarget.getBoundingClientRect(),
                            })}
                            onMouseLeave={() => setHoveredCell(null)}
                          />
                        );
                      })}
                    </div>

                    {(!panel || panel.status !== "ready") && (
                      <div className={`gantt-row-status${panel?.status === "error" ? " gantt-row-status--error" : ""}`}>
                        {panel?.status === "error" ? (
                          <>
                            <span>{panel.error.text}</span>
                            {panel.error.hint && <span className="gantt-row-hint">{panel.error.hint}</span>}
                            <button
                              type="button"
                              className="gantt-row-retry"
                              onClick={() => retry(target.key)}
                            >
                              Повторить
                            </button>
                          </>
                        ) : "Загрузка…"}
                      </div>
                    )}
                    {panel?.fallback && (
                      <div className="gantt-row-status">
                        Кадров за период слишком много — показано интервалами
                      </div>
                    )}
                    {panel?.status === "ready" && segments.length === 0 && (
                      <div className="gantt-row-status">Нет наблюдений за период</div>
                    )}
                  </div>
                );
              })}
            </div>

            {hoveredCell && (
              <div
                ref={tooltipRef}
                className="gantt-tooltip"
                style={{
                  position: "fixed",
                  left: Math.min(hoveredCell.rect.right + 10, window.innerWidth - 180),
                  top: hoveredCell.rect.top - 10,
                  zIndex: 9999,
                }}
              >
                <div className="gantt-tooltip-cam">{hoveredCell.title}</div>
                <div className="gantt-tooltip-time">
                  {formatShortInTimezone(hoveredCell.segment.atIso, hoveredCell.timezone)}
                  {hoveredCell.timezone ? ` (${hoveredCell.timezone})` : ""}
                </div>
                <div className="gantt-tooltip-count">{formatMetricValue(hoveredCell.segment)}</div>
              </div>
            )}

            <div className="gantt-legend">
              <span className="gantt-legend-label">0</span>
              <div className="gantt-legend-gradient" />
              <span className="gantt-legend-label">макс</span>
              <span className="gantt-legend-hint">значение метрики панели</span>
            </div>
            <div className="gantt-mini-legend">
              <div className="gantt-mini-legend-item">
                <span className="gantt-mini-legend-dot gantt-mini-legend-dot--soft" />
                <span>Светлее цвет — меньше значение метрики</span>
              </div>
              <div className="gantt-mini-legend-item">
                <span className="gantt-mini-legend-dot gantt-mini-legend-dot--strong" />
                <span>Насыщеннее красный — больше значение</span>
              </div>
              <div className="gantt-mini-legend-item">
                <span className="gantt-mini-legend-mark" />
                <span>Серая штриховка — наблюдение без данных (unknown)</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="gantt-empty">
          {scopeTooWide
            ? "Отметьте камеры и нажмите «Применить» — каждая камера строится отдельной панелью."
            : "Укажите корректный период, чтобы увидеть диаграмму."}
        </div>
      )}
    </div>
  );
}
